import type { MembershipCardPrepay } from '@/services/memberships.service';

export interface PatientSidebarTag {
  patientsTagsCustomID: number;
  tagsCustomID: number;
  title: string;
  description: string;
  icon: string;
  createDate: string;
  addedByName: string;
}

export interface PatientSidebarMembership {
  recurringId: number;
  title: string;
  paymentThroughDate: string;
  firstBillingDate: string;
  nextBillingDate: string;
  incrementType: string;
  declined: boolean;
  statusText: string;
  section: 'upcoming' | 'active' | 'frozen' | 'cancelling';
  /** The membership's credits, as its detail counts them. Null once it has ended. */
  prepay: MembershipCardPrepay | null;
}

export interface PatientSidebarAppointment {
  title: string;
  startTime: string;
  providerName: string | null;
  facilityName: string | null;
  /** IANA zone of the appointment's facility — `startTime` is UTC and must be
   *  displayed in the facility's clock, same as the Appointments tab cards. */
  facilityIanaTimezone: string | null;
  appointmentId: number | null;
  /** Null when the patient was never checked out — one of the "Action Required"
   *  conditions the Recent Visits cards surface. See lib/appointmentActions. */
  checkedOutAt: string | null;
  noShow: boolean;
}

export interface PatientDetail {
  patient: Record<string, unknown> | null;
  tags: PatientSidebarTag[];
  note: string;
  memberships: PatientSidebarMembership[];
  upcomingAppointments: PatientSidebarAppointment[];
  recentAppointments: PatientSidebarAppointment[];
}

export const EMPTY_PATIENT_DETAIL: PatientDetail = {
  patient: null,
  tags: [],
  note: '',
  memberships: [],
  upcomingAppointments: [],
  recentAppointments: [],
};

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function asBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes';
  }
  return false;
}

function unwrapData<T>(payload: unknown): T | null {
  if (isRecord(payload) && 'data' in payload) {
    return (payload.data as T) ?? null;
  }
  return (payload as T) ?? null;
}

function getRecord(value: unknown): UnknownRecord | null {
  return isRecord(value) ? value : null;
}

function getMembershipPrepay(value: unknown): MembershipCardPrepay | null {
  const prepay = getRecord(value);
  if (!prepay) return null;
  return {
    usableCredits: Number(prepay.usableCredits) || 0,
    paidThroughDate: asString(prepay.paidThroughDate),
    nextChargeDate: asString(prepay.nextChargeDate),
    keepThroughPending: asBoolean(prepay.keepThroughPending),
    keepThroughEndDate: asString(prepay.keepThroughEndDate),
  };
}

function getTagTitle(tag: unknown): string | null {
  if (typeof tag === 'string') return asString(tag);
  const record = getRecord(tag);
  if (!record) return null;
  return asString(record.title) ?? asString(record.name) ?? asString(record.label);
}

function getMembershipTemplateId(membership: UnknownRecord): string | null {
  const direct = asString(membership.membershipTemplateID) ?? asString(membership.membershipTemplateId);
  if (direct) return direct;
  const numeric = membership.membershipTemplateID ?? membership.membershipTemplateId;
  return numeric == null ? null : String(numeric);
}

function buildMembershipTemplateMap(payload: unknown): Map<string, string> {
  const list = unwrapData<unknown[]>(payload);
  const templateMap = new Map<string, string>();

  if (!Array.isArray(list)) return templateMap;

  list.forEach((item) => {
    const record = getRecord(item);
    if (!record) return;
    const templateId =
      asString(record.membershipTemplateID) ??
      asString(record.membershipTemplateId) ??
      (record.membershipTemplateID == null ? null : String(record.membershipTemplateID));
    const title = asString(record.title);
    if (templateId && title) {
      templateMap.set(templateId, title);
    }
  });

  return templateMap;
}

function getMembershipTitle(membership: UnknownRecord, templateMap: Map<string, string>): string {
  const template = getRecord(membership.template);
  const templateId = getMembershipTemplateId(membership);

  return (
    asString(membership.title) ??
    asString(template?.title) ??
    (templateId ? templateMap.get(templateId) : null) ??
    (templateId ? `Membership #${templateId}` : 'Membership')
  );
}

function getAppointmentStartTime(appointment: UnknownRecord): string | null {
  return (
    asString(appointment.startTime) ??
    asString(appointment.scheduledAppointmentStartTime) ??
    asString(appointment.scheduledAppointmentDateTime)
  );
}

function getAppointmentProviderName(appointment: UnknownRecord): string | null {
  const providerFirst = asString(appointment.providerFirstName);
  const providerLast = asString(appointment.providerLastName);
  const providerFull = [providerFirst, providerLast].filter(Boolean).join(' ').trim();

  if (providerFull) return providerFull;

  const directProvider =
    asString(appointment.providerName) ??
    asString(appointment.resourceName) ??
    asString(appointment.doctorName);
  if (directProvider) return directProvider;

  const resources = Array.isArray(appointment.resources) ? appointment.resources : [];
  const resourceNames = resources
    .map((resource) => getRecord(resource))
    .map((resource) => (resource ? asString(resource.resourceName) ?? asString(resource.name) : null))
    .filter((name): name is string => Boolean(name));

  return resourceNames.length > 0 ? resourceNames.join(', ') : null;
}

function getAppointmentTitle(appointment: UnknownRecord, fallback: string): string {
  const appointmentType = getRecord(appointment.appointmentType);
  const candidates = [
    asString(appointmentType?.appointmentTypeName),
    asString(appointment.appointmentTypeName),
    asString(appointment.scheduledAppointmentName),
    asString(appointment.appointmentName),
    asString(appointment.name),
    asString(appointment.title),
  ].filter((value): value is string => Boolean(value));

  const specific = candidates.find((candidate) => {
    const normalized = candidate.trim().toLowerCase();
    return normalized !== 'visit' && normalized !== 'appointment';
  });

  return specific ?? candidates[0] ?? fallback;
}

function isCancelledAppointment(appointment: UnknownRecord): boolean {
  const status = asString(appointment.status)?.toLowerCase();
  return (
    status === 'cancelled' ||
    asBoolean(appointment.cancelled) ||
    asBoolean(appointment.scheduledAppointmentCancelled)
  );
}

export function normalizePatientTags(payload: unknown): PatientSidebarTag[] {
  const list = unwrapData<unknown[]>(payload);
  if (!Array.isArray(list)) return [];

  return list
    .map((item) => {
      const title = getTagTitle(item);
      if (!title) return null;
      const record = getRecord(item);
      const description = record ? asString(record.description) ?? '' : '';
      const rawPtcId = record?.patientsTagsCustomID;
      const patientsTagsCustomID = rawPtcId != null ? Number(rawPtcId) : 0;
      const rawTcId = record?.tagsCustomID;
      const tagsCustomID = rawTcId != null ? Number(rawTcId) : 0;
      const icon = record ? asString(record.icon) ?? '' : '';
      const createDate = record ? asString(record.createDate) ?? '' : '';
      const addedByName = record ? asString(record.addedByName) ?? '' : '';
      return { patientsTagsCustomID, tagsCustomID, title, description, icon, createDate, addedByName };
    })
    .filter((tag): tag is PatientSidebarTag => tag !== null);
}

export function normalizePatientMemberships(
  membershipsPayload: unknown,
  templatesPayload: unknown,
): PatientSidebarMembership[] {
  const list = unwrapData<unknown[]>(membershipsPayload);
  if (!Array.isArray(list)) return [];

  const templateMap = buildMembershipTemplateMap(templatesPayload);

  return list
    .map((item) => getRecord(item))
    .filter((membership): membership is UnknownRecord => Boolean(membership))
    .map((membership) => {
      const incrementType = asString(membership.incrementType) ?? '1';
      const declined = asString(membership.declined) === '1';
      const rawStatus = (asString(membership.status) ?? '').toLowerCase();
      const paymentStatus = (asString(membership.paymentStatusDisplay) ?? asString(membership.paymentStatus) ?? '').toLowerCase();
      const isFrozen = rawStatus === 'frozen' || paymentStatus === 'frozen';
      const statusText = incrementType === '0'
        ? 'Inactive'
        : isFrozen
          ? 'Frozen'
          : declined
            ? 'Declined'
            : 'Active';

      const rawId = membership.paymentsRecurringAuthnetID ?? membership.recurringId ?? 0;
      const firstBillingDate = asString(membership.firstBillingDate) ?? '';
      const nextBillingDate = asString(membership.nextBillingDate) ?? '';
      const todayStr = new Date().toISOString().slice(0, 10);
      const firstYmd = firstBillingDate.slice(0, 10);

      const paymentThroughDate = asString(membership.paymentThroughDate) ?? '';
      const throughYmd = paymentThroughDate.slice(0, 10);
      const prepay = getMembershipPrepay(membership.prepay);

      let section: 'upcoming' | 'active' | 'frozen' | 'cancelling';
      // Cancelled with "keep": it bills no more and ends when its credits run out.
      if (prepay?.keepThroughPending) {
        section = 'cancelling';
      } else if (incrementType === '0') {
        section = throughYmd && throughYmd >= todayStr ? 'cancelling' : 'active';
      } else if (statusText === 'Frozen') {
        section = 'frozen';
      } else if (firstYmd && firstYmd > todayStr) {
        section = 'upcoming';
      } else {
        section = 'active';
      }

      return {
        recurringId: typeof rawId === 'number' ? rawId : Number(rawId) || 0,
        title:
          asString(membership.membershipTitle) ||
          getMembershipTitle(membership, templateMap),
        paymentThroughDate,
        firstBillingDate,
        nextBillingDate,
        incrementType,
        declined,
        statusText,
        section,
        prepay,
      };
    })
    .filter((m) => m.incrementType !== '0' || m.section === 'cancelling');
}

export function normalizePatientAppointments(payload: unknown): Pick<
  PatientDetail,
  'upcomingAppointments' | 'recentAppointments'
> {
  const list = unwrapData<unknown[]>(payload);
  const upcomingAppointments: PatientSidebarAppointment[] = [];
  const recentAppointments: PatientSidebarAppointment[] = [];

  if (!Array.isArray(list)) {
    return { upcomingAppointments, recentAppointments };
  }

  const now = Date.now();

  list
    .map((item) => getRecord(item))
    .filter((appointment): appointment is UnknownRecord => Boolean(appointment))
    .filter((appointment) => !isCancelledAppointment(appointment))
    .forEach((appointment) => {
      const startTime = getAppointmentStartTime(appointment);
      if (!startTime) return;

      const rawId = appointment.appointmentId ?? appointment.scheduledAppointmentsID;
      const appointmentId = rawId != null ? Number(rawId) : null;

      const facilityRecord = getRecord(appointment.facility);
      const facilityName =
        asString(facilityRecord?.facilityName) ??
        asString(facilityRecord?.name) ??
        asString(appointment.facilityName) ??
        null;

      const facilityIanaTimezone =
        asString(appointment.facilityIanaTimezone) ??
        asString(facilityRecord?.ianaTimezone) ??
        asString(facilityRecord?.timezone) ??
        null;

      const normalized: PatientSidebarAppointment = {
        title: getAppointmentTitle(appointment, 'Appointment'),
        startTime,
        providerName: getAppointmentProviderName(appointment),
        facilityName,
        facilityIanaTimezone,
        appointmentId: appointmentId && !isNaN(appointmentId) ? appointmentId : null,
        checkedOutAt:
          asString(appointment.checkedOutAt) ??
          asString(appointment.scheduledAppointmentStatusCheckOutTime),
        noShow: asBoolean(appointment.noShow) || asBoolean(appointment.scheduledAppointmentNoShow),
      };

      if (new Date(startTime).getTime() >= now) {
        upcomingAppointments.push(normalized);
      } else {
        recentAppointments.push(normalized);
      }
    });

  upcomingAppointments.sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  );
  recentAppointments.sort(
    (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
  );

  return {
    upcomingAppointments: upcomingAppointments.slice(0, 5),
    recentAppointments: recentAppointments.slice(0, 4),
  };
}
